import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import SearchableSelect from './SearchableSelect';
import FleetManagement from './FleetManagement';
import CSVImportModal from './CSVImportModal';
import Pagination from './Pagination';
import './css/BaobabDesignSystem.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const TransporteursModule = () => {
  const { user } = useAuth();
  const [transporteurs, setTransporteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFleetModal, setShowFleetModal] = useState(false);
  const [selectedTransporteur, setSelectedTransporteur] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [truckTypes, setTruckTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('transporteurs');
  const [showImportModal, setShowImportModal] = useState(false);

  // Batch selection states
  const [selectedTransporteurs, setSelectedTransporteurs] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    cni: '',
    specialties: [],
    vehicle_types: [],
    notes: '',
    type_transporteur: 'independant' // independant, representant, societe
  });

  const specialtiesOptions = [
    'maritime',
    'routier',
    'aérien',
    'ferroviaire'
  ];

  const typeTransporteurOptions = [
    { value: 'independant', label: '👤 Chauffeur Indépendant' },
    { value: 'representant', label: '🤝 Représentant de Chauffeurs' },
    { value: 'societe', label: '🏢 Société de Transport' }
  ];

  useEffect(() => {
    fetchTransporteurs();
    fetchTruckTypes();
  }, []);

  // Charger les types de camions depuis l'API
  const fetchTruckTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/truck-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Formater pour SearchableSelect
        const formatted = (data.truck_types || []).map(truck => ({
          value: truck.type,
          label: truck.type,
          type: truck.type
        }));
        setTruckTypes(formatted);
      }
    } catch (error) {
      console.error('Erreur chargement types camions:', error);
    }
  };

  const fetchTransporteurs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transporters`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransporteurs(data);
      } else {
        console.error('Erreur lors de la récupération des transporteurs');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransporteur = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/transporters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Transporteur créé avec succès !');
        setShowCreateModal(false);
        resetForm();
        fetchTransporteurs();
      } else {
        alert('Erreur lors de la création du transporteur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création du transporteur');
    }
  };

  const handleEditTransporteur = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/transporters/${selectedTransporteur.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Transporteur modifié avec succès !');
        setShowEditModal(false);
        setSelectedTransporteur(null);
        resetForm();
        fetchTransporteurs();
      } else {
        alert('Erreur lors de la modification du transporteur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification du transporteur');
    }
  };

  const handleDeleteTransporteur = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transporters/${selectedTransporteur.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Transporteur supprimé avec succès !');
        setShowDeleteModal(false);
        setSelectedTransporteur(null);
        fetchTransporteurs();
      } else {
        alert('Erreur lors de la suppression du transporteur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du transporteur');
    }
  };

  // Batch operations
  const handleSelectTransporteur = (transporteur) => {
    setSelectedTransporteurs(prev => {
      if (prev.find(t => t.id === transporteur.id)) {
        return prev.filter(t => t.id !== transporteur.id);
      } else {
        return [...prev, transporteur];
      }
    });
  };

  const handleSelectAllTransporteurs = (checked) => {
    if (checked) {
      setSelectedTransporteurs(filteredTransporteurs());
    } else {
      setSelectedTransporteurs([]);
    }
  };

  const handleBatchDelete = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedTransporteurs.length} transporteur(s) ?`)) {
      try {
        const deletePromises = selectedTransporteurs.map(transporteur => 
          fetch(`${API_BASE_URL}/api/transporters/${transporteur.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        );
        
        await Promise.all(deletePromises);
        alert(`${selectedTransporteurs.length} transporteur(s) supprimé(s) avec succès !`);
        setSelectedTransporteurs([]);
        fetchTransporteurs();
      } catch (error) {
        console.error('Erreur lors de la suppression en lot:', error);
        alert('Erreur lors de la suppression des transporteurs');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      cni: '',
      specialties: [],
      vehicle_types: [],
      notes: '',
      type_transporteur: 'independant'
    });
  };

  const openEditModal = (transporteur) => {
    setSelectedTransporteur(transporteur);
    setFormData({
      name: transporteur.name || '',
      contact_person: transporteur.contact_person || '',
      email: transporteur.email || '',
      phone: transporteur.phone || '',
      cni: transporteur.cni || '',
      specialties: transporteur.specialties || [],
      vehicle_types: transporteur.vehicle_types || [],
      notes: transporteur.notes || '',
      type_transporteur: transporteur.type_transporteur || 'independant'
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (transporteur) => {
    setSelectedTransporteur(transporteur);
    setShowDeleteModal(true);
  };

  const openFleetModal = (transporteur) => {
    setSelectedTransporteur(transporteur);
    setShowFleetModal(true);
  };

  const handleExportCSV = async () => {
    try {
      const csvContent = [
        ['Nom', 'Contact', 'Téléphone', 'Email', 'Type', 'Spécialités', 'Types de véhicules', 'Nombre de camions'],
        ...transporteurs.map(t => [
          t.name,
          t.contact_person,
          t.phone || '',
          t.email || '',
          getTransporteurTypeLabel(t.type_transporteur).split(' ').slice(1).join(' '),
          (t.specialties || []).join(', '),
          (t.vehicle_types || []).join(', '),
          t.nombre_camions || 0
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transporteurs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export CSV');
    }
  };

  const filteredTransporteurs = () => {
    return transporteurs.filter(transporteur => {
      const matchesSearch = transporteur.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           transporteur.contact_person.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSpecialty = filterSpecialty === '' || 
                              transporteur.specialties.includes(filterSpecialty);
      return matchesSearch && matchesSpecialty;
    });
  };

  const handleSpecialtyChange = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleVehicleTypeChange = (vehicleType) => {
    setFormData(prev => ({
      ...prev,
      vehicle_types: prev.vehicle_types.includes(vehicleType)
        ? prev.vehicle_types.filter(v => v !== vehicleType)
        : [...prev.vehicle_types, vehicleType]
    }));
  };

  const getTransporteurTypeIcon = (type) => {
    switch (type) {
      case 'independant': return '👤';
      case 'representant': return '🤝';
      case 'societe': return '🏢';
      default: return '🚛';
    }
  };

  const getTransporteurTypeLabel = (type) => {
    const option = typeTransporteurOptions.find(opt => opt.value === type);
    return option ? option.label : 'Transporteur';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Chargement des transporteurs...</div>
      </div>
    );
  }

  return (
    <>
      {/* Show Fleet Management if active */}
      {showFleetModal && selectedTransporteur && (
        <FleetManagement
          transporteurId={selectedTransporteur.id}
          transporteurName={selectedTransporteur.name}
          onClose={() => {
            setShowFleetModal(false);
            setSelectedTransporteur(null);
          }}
        />
      )}

      {/* Normal Transporteurs View */}
      {!showFleetModal && (
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Transporteurs</h1>
              <p className="text-gray-600 mt-1">
                Gérez vos chauffeurs indépendants, représentants et sociétés de transport
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                title="Exporter en CSV"
              >
                📊 Export CSV
              </button>
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {viewMode === 'grid' ? '📋 Vue tableau' : '🔲 Vue grille'}
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                📥 Import CSV
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                ➕ Nouveau Transporteur
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Total Transporteurs</h3>
              <p className="text-2xl font-bold text-green-600">{transporteurs.length}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Chauffeurs Indépendants</h3>
              <p className="text-2xl font-bold text-blue-600">
                {transporteurs.filter(t => t.type_transporteur === 'independant').length}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Sociétés</h3>
              <p className="text-2xl font-bold text-purple-600">
                {transporteurs.filter(t => t.type_transporteur === 'societe').length}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">Total Véhicules</h3>
              <p className="text-2xl font-bold text-orange-600">
                {transporteurs.reduce((sum, t) => sum + (t.nombre_camions || 0), 0)}
              </p>
            </div>
          </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Rechercher par nom ou contact..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 min-w-64 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <select
          value={filterSpecialty}
          onChange={(e) => setFilterSpecialty(e.target.value)}
          className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="">Toutes les spécialités</option>
          {specialtiesOptions.map(specialty => (
            <option key={specialty} value={specialty}>
              {specialty.charAt(0).toUpperCase() + specialty.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{filteredTransporteurs().length}</div>
          <div className="text-sm text-blue-800">Total transporteurs</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {filteredTransporteurs().filter(t => t.specialties.includes('routier')).length}
          </div>
          <div className="text-sm text-green-800">Transport routier</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-orange-600">
            {filteredTransporteurs().filter(t => t.specialties.includes('maritime')).length}
          </div>
          <div className="text-sm text-orange-800">Transport maritime</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">
            {filteredTransporteurs().filter(t => t.vehicle_types.length > 0).length}
          </div>
          <div className="text-sm text-purple-800">Avec véhicules</div>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {selectedTransporteurs.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedTransporteurs.length} transporteur(s) sélectionné(s)
              </span>
              <button
                onClick={() => setSelectedTransporteurs([])}
                className="text-sm text-green-600 hover:text-green-800"
              >
                Désélectionner tout
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBatchDelete}
                className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTransporteurs().slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((transporteur) => (
            <div key={transporteur.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow relative">
              {/* Checkbox for selection */}
              <div className="absolute top-3 left-3">
                <input
                  type="checkbox"
                  checked={selectedTransporteurs.some(t => t.id === transporteur.id)}
                  onChange={() => handleSelectTransporteur(transporteur)}
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
              </div>

              <div className="pt-4">
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-2">
                    {getTransporteurTypeIcon(transporteur.type_transporteur)}
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{transporteur.name}</h3>
                    <p className="text-sm text-gray-600">{transporteur.contact_person}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {transporteur.phone && (
                    <p className="text-sm text-gray-600">📞 {transporteur.phone}</p>
                  )}
                  {transporteur.email && (
                    <p className="text-sm text-gray-600">✉️ {transporteur.email}</p>
                  )}
                  {transporteur.cni && (
                    <p className="text-sm text-gray-600">🆔 {transporteur.cni}</p>
                  )}
                </div>

                {transporteur.specialties.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Spécialités:</p>
                    <div className="flex flex-wrap gap-1">
                      {transporteur.specialties.map(specialty => (
                        <span key={specialty} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {transporteur.vehicle_types.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Types de véhicules:</p>
                    <div className="flex flex-wrap gap-1">
                      {transporteur.vehicle_types.slice(0, 3).map(vehicle => (
                        <span key={vehicle} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {vehicle}
                        </span>
                      ))}
                      {transporteur.vehicle_types.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{transporteur.vehicle_types.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => openFleetModal(transporteur)}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    🚛 Flotte ({transporteur.nombre_camions || 0})
                  </button>
                  <button
                    onClick={() => openEditModal(transporteur)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    onClick={() => openDeleteModal(transporteur)}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-green-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedTransporteurs.length === filteredTransporteurs().length && filteredTransporteurs().length > 0}
                    onChange={(e) => handleSelectAllTransporteurs(e.target.checked)}
                    className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Spécialités</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransporteurs().slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((transporteur) => (
                <tr key={transporteur.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedTransporteurs.some(t => t.id === transporteur.id)}
                      onChange={() => handleSelectTransporteur(transporteur)}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{transporteur.name}</div>
                    <div className="text-sm text-gray-500">{transporteur.contact_person}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{transporteur.phone}</div>
                    <div className="text-sm text-gray-500">{transporteur.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                      {getTransporteurTypeIcon(transporteur.type_transporteur)} {getTransporteurTypeLabel(transporteur.type_transporteur).split(' ').slice(1).join(' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {transporteur.specialties.slice(0, 2).map(specialty => (
                        <span key={specialty} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          {specialty}
                        </span>
                      ))}
                      {transporteur.specialties.length > 2 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          +{transporteur.specialties.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openFleetModal(transporteur)}
                        className="text-green-600 hover:text-green-900"
                      >
                        🚛 Flotte ({transporteur.nombre_camions || 0})
                      </button>
                      <button
                        onClick={() => openEditModal(transporteur)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        onClick={() => openDeleteModal(transporteur)}
                        className="text-red-600 hover:text-red-900"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredTransporteurs().length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🚛</div>
          <p className="text-gray-500 text-lg">Aucun transporteur trouvé</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Créer le premier transporteur
          </button>
        </div>
      )}

      {/* Pagination */}
      {filteredTransporteurs().length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(filteredTransporteurs().length / itemsPerPage)}
          totalItems={filteredTransporteurs().length}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Nouveau Transporteur</h2>
              <button
                onClick={() => {setShowCreateModal(false); resetForm();}}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateTransporteur} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom / Raison sociale *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personne de contact *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de transporteur</label>
                <div className="flex flex-wrap gap-2">
                  {typeTransporteurOptions.map(option => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="type_transporteur"
                        value={option.value}
                        checked={formData.type_transporteur === option.value}
                        onChange={(e) => setFormData({...formData, type_transporteur: e.target.value})}
                        className="mr-2"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CNI / Numéro d'identification</label>
                <input
                  type="text"
                  value={formData.cni}
                  onChange={(e) => setFormData({...formData, cni: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Spécialités transport</label>
                <div className="grid grid-cols-2 gap-2">
                  {specialtiesOptions.map(specialty => (
                    <label key={specialty} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.specialties.includes(specialty)}
                        onChange={() => handleSpecialtyChange(specialty)}
                        className="mr-2"
                      />
                      {specialty.charAt(0).toUpperCase() + specialty.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Types de véhicules</label>
                <SearchableSelect
                  options={truckTypes}
                  selectedValues={formData.vehicle_types}
                  onMultipleChange={(values) => setFormData({...formData, vehicle_types: values})}
                  placeholder="Rechercher des types de véhicules..."
                  multiple={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Notes additionnelles, certifications, etc..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {setShowCreateModal(false); resetForm();}}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Créer le transporteur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTransporteur && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Modifier Transporteur</h2>
              <button
                onClick={() => {setShowEditModal(false); setSelectedTransporteur(null); resetForm();}}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditTransporteur} className="space-y-4">
              {/* Same form fields as create modal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom / Raison sociale *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personne de contact *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de transporteur</label>
                <div className="flex flex-wrap gap-2">
                  {typeTransporteurOptions.map(option => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="type_transporteur"
                        value={option.value}
                        checked={formData.type_transporteur === option.value}
                        onChange={(e) => setFormData({...formData, type_transporteur: e.target.value})}
                        className="mr-2"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CNI / Numéro d'identification</label>
                <input
                  type="text"
                  value={formData.cni}
                  onChange={(e) => setFormData({...formData, cni: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Spécialités transport</label>
                <div className="grid grid-cols-2 gap-2">
                  {specialtiesOptions.map(specialty => (
                    <label key={specialty} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.specialties.includes(specialty)}
                        onChange={() => handleSpecialtyChange(specialty)}
                        className="mr-2"
                      />
                      {specialty.charAt(0).toUpperCase() + specialty.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Types de véhicules</label>
                <SearchableSelect
                  options={truckTypes}
                  selectedValues={formData.vehicle_types}
                  onMultipleChange={(values) => setFormData({...formData, vehicle_types: values})}
                  placeholder="Rechercher des types de véhicules..."
                  multiple={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Notes additionnelles, certifications, etc..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {setShowEditModal(false); setSelectedTransporteur(null); resetForm();}}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Sauvegarder les modifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedTransporteur && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-red-600">Confirmer la suppression</h2>
              <button 
                onClick={() => {setShowDeleteModal(false); setSelectedTransporteur(null);}}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Êtes-vous sûr de vouloir supprimer ce transporteur ?
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {selectedTransporteur.name}
                </p>
                <p className="text-sm text-gray-600">
                  Contact: {selectedTransporteur.contact_person}
                </p>
              </div>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">
                  Cette action est irréversible. Le transporteur sera désactivé.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {setShowDeleteModal(false); setSelectedTransporteur(null);}}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteTransporteur}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      )}

      {/* Import CSV Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        type="transporters"
        onImportComplete={() => {
          fetchTransporteurs(); // Refresh the list after import
          setShowImportModal(false);
        }}
      />
    </>
  );
};

export default TransporteursModule;